import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import {
  getSelfServiceContentFallback,
  hydrateSelfServiceContent
} from '../data/selfServiceContent';

const initialFormState = {
  subject: '',
  category: 'account',
  urgency: 'medium',
  description: '',
  location: '',
  preferredContact: 'email'
};

const statusLabels = {
  open: 'Açık',
  in_progress: 'İşlemde',
  waiting_user: 'Kullanıcı bekleniyor',
  resolved: 'Çözüldü',
  closed: 'Kapandı'
};

const urgencyBadgeClass = {
  low: 'bg-light text-dark',
  medium: 'bg-info-subtle text-info-emphasis',
  high: 'bg-warning-subtle text-warning-emphasis',
  critical: 'bg-danger-subtle text-danger-emphasis'
};

const getCurrentLunchMenu = (menuItems) => {
  if (!menuItems.length) {
    return null;
  }

  const weekdayIndex = new Date().getDay();
  const weekdayMap = {
    1: 'Pazartesi',
    2: 'Salı',
    3: 'Çarşamba',
    4: 'Perşembe',
    5: 'Cuma'
  };

  const todayMenu = menuItems.find((item) => item.day === weekdayMap[weekdayIndex]);
  return todayMenu || menuItems[0];
};

const SelfServicePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(initialFormState);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    open: 0,
    inProgress: 0,
    resolved: 0,
    total: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(() => getSelfServiceContentFallback());
  const { toasts, addToast, removeToast } = useToast();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const activeCompanyLabel = useMemo(() => {
    if (currentUser.role === 'super_admin') {
      return localStorage.getItem('activeCompanyName') || 'Seçili firma';
    }

    return currentUser.company || 'Kendi firmanız';
  }, [currentUser.company, currentUser.role]);

  const activeCompanyId = useMemo(
    () =>
      currentUser.role === 'super_admin'
        ? localStorage.getItem('activeCompanyId') || ''
        : currentUser.companyId || '',
    [currentUser.companyId, currentUser.role]
  );

  useEffect(() => {
    if (!location.hash || loading) {
      return;
    }

    const sectionId = location.hash.replace('#', '');
    const section = document.getElementById(sectionId);

    if (section) {
      window.setTimeout(() => {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [location.hash, loading]);

  useEffect(() => {
    const loadSupportData = async () => {
      try {
        setLoading(true);

        const [ticketResponse, statsResponse, contentResponse] = await Promise.all([
          api.getITSupportTickets(),
          api.getITSupportStats(),
          api.getManagedContent('selfService')
        ]);

        setTickets(ticketResponse.tickets || []);
        setStats(
          statsResponse.data || {
            open: 0,
            inProgress: 0,
            resolved: 0,
            total: 0
          }
        );
        setContent(
          contentResponse?.success
            ? hydrateSelfServiceContent(contentResponse.data || {})
            : getSelfServiceContentFallback()
        );
      } catch (error) {
        console.error('Self-service verileri yuklenemedi:', error);
        setContent(getSelfServiceContentFallback());
        addToast('Operasyonel servis verileri yüklenemedi', 'error', 5000);
      } finally {
        setLoading(false);
      }
    };

    loadSupportData();
  }, [addToast, activeCompanyId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);

      const payload = {
        ...formData,
        requesterName:
          `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
          currentUser.name ||
          'Kullanıcı',
        requesterEmail: currentUser.email || localStorage.getItem('userEmail') || ''
      };

      const response = await api.createITSupportTicket(payload);

      if (response.success) {
        addToast('IT destek talebiniz kaydedildi', 'success', 4000);
        setFormData(initialFormState);

        const [ticketResponse, statsResponse] = await Promise.all([
          api.getITSupportTickets(),
          api.getITSupportStats()
        ]);

        setTickets(ticketResponse.tickets || []);
        setStats(
          statsResponse.data || {
            open: 0,
            inProgress: 0,
            resolved: 0,
            total: 0
          }
        );
      }
    } catch (error) {
      console.error('IT destek talebi olusturulamadi:', error);
      addToast('IT destek talebi oluşturulurken bir hata oluştu', 'error', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const lunchMenu = content.lunchMenu || [];
  const shuttleRoutes = content.shuttleRoutes || [];
  const expenseLinks = content.expenseLinks || [];
  const techFaqs = content.techFaqs || [];
  const operationsContacts = content.operationsContacts || [];
  const supportCategories = content.supportCategories || [];
  const urgencyOptions = content.urgencyOptions || [];
  const currentLunchMenu = getCurrentLunchMenu(lunchMenu);

  return (
    <div className="modern-dashboard self-service-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="corporate-hero-card self-service-hero">
            <div>
              <span className="corporate-hero-badge">Operasyonel self-service</span>
              <div className="mt-2">
                <small className="badge bg-light text-dark">Aktif firma: {activeCompanyLabel}</small>
              </div>
              <h1>Günlük ihtiyaçlar, destek ve operasyonel erişimler tek merkezde</h1>
              <p>
                Yemekhane menüsü, servis bilgileri, masraf yönlendirmeleri, teknik SSS ve IT
                destek taleplerinizi bu ekrandan yönetebilirsiniz.
              </p>
            </div>
            <div className="self-service-stats">
              <div className="self-service-stat-card">
                <strong>{stats.total}</strong>
                <span>Toplam ticket</span>
              </div>
              <div className="self-service-stat-card">
                <strong>{stats.open + stats.inProgress}</strong>
                <span>Aktif destek</span>
              </div>
              <div className="self-service-stat-card">
                <strong>{stats.resolved}</strong>
                <span>Çözülen kayıt</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div id="yemekhane" className="col-xl-4 col-lg-6 mb-4 scroll-margin-section">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-dish"></i>
                <h4>Yemekhane Menüsü</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              {currentLunchMenu && (
                <div className="self-service-list-card mb-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Bugünün menüsü</h6>
                    <span className="badge text-bg-light">{currentLunchMenu.day}</span>
                  </div>
                  <strong className="d-block">{currentLunchMenu.main}</strong>
                  <small className="text-muted d-block">
                    {currentLunchMenu.side} - {currentLunchMenu.serviceWindow}
                  </small>
                </div>
              )}

              <div className="self-service-list">
                {lunchMenu.map((menu) => (
                  <div key={menu.day} className="self-service-list-card">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">{menu.day}</h6>
                      <span className="badge text-bg-light">Haftalık</span>
                    </div>
                    <p className="mb-1">{menu.soup}</p>
                    <strong className="d-block mb-1">{menu.main}</strong>
                    <small className="text-muted d-block">{menu.side}</small>
                    <small className="text-muted">{menu.extra}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div id="servis" className="col-xl-4 col-lg-6 mb-4 scroll-margin-section">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-bus"></i>
                <h4>Servis ve Ulaşım</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="self-service-list">
                {shuttleRoutes.map((route) => (
                  <div key={route.id} className="self-service-list-card">
                    <h6>{route.title}</h6>
                    <p>{route.summary}</p>
                    <small className="d-block text-muted">Kalkış: {route.departure}</small>
                    <small className="d-block text-muted">Dönüş: {route.returnTime}</small>
                    <small className="d-block mt-2">{route.notes}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-lg-12 mb-4">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-wallet-alt"></i>
                <h4>Masraf ve Harcama Akışları</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="self-service-link-list">
                {expenseLinks.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    className="self-service-link-card"
                    onClick={() => {
                      if (link.external) {
                        window.open(link.path, '_blank', 'noopener,noreferrer');
                        return;
                      }

                      navigate(link.path);
                    }}
                  >
                    <i className={link.icon}></i>
                    <div>
                      <h6>{link.title}</h6>
                      <p>{link.description}</p>
                      <span>{link.ctaLabel}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div id="it-destek" className="col-xl-7 mb-4 scroll-margin-section">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-bug"></i>
                <h4>IT Destek Talep Formu</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <form onSubmit={handleSubmit} className="modern-hr-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Konu</label>
                      <input
                        type="text"
                        className="modern-hr-form-input"
                        placeholder="Örn. VPN bağlantısı kurulamıyor"
                        value={formData.subject}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, subject: event.target.value }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Kategori</label>
                      <select
                        className="modern-hr-form-select"
                        value={formData.category}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, category: event.target.value }))
                        }
                      >
                        {supportCategories.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Öncelik</label>
                      <select
                        className="modern-hr-form-select"
                        value={formData.urgency}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, urgency: event.target.value }))
                        }
                      >
                        {urgencyOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Sorun Açıklaması</label>
                  <textarea
                    rows="5"
                    className="modern-hr-form-textarea"
                    placeholder="Sorunun ne zaman başladığını, hata mesajını ve etkilediği işi yazın."
                    value={formData.description}
                    onChange={(event) =>
                      setFormData((prev) => ({ ...prev, description: event.target.value }))
                    }
                    required
                  />
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Lokasyon</label>
                      <input
                        type="text"
                        className="modern-hr-form-input"
                        placeholder="Merkez ofis 5. kat, şantiye ofisi vb."
                        value={formData.location}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, location: event.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="modern-hr-form-group">
                      <label className="modern-hr-form-label">Tercih edilen iletişim</label>
                      <select
                        className="modern-hr-form-select"
                        value={formData.preferredContact}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            preferredContact: event.target.value
                          }))
                        }
                      >
                        <option value="email">E-posta</option>
                        <option value="phone">Telefon</option>
                        <option value="teams">Teams</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="modern-hr-form-actions">
                  <button
                    type="submit"
                    className="modern-hr-btn modern-hr-btn-primary"
                    disabled={submitting}
                  >
                    <i className="bx bx-send"></i>
                    {submitting ? 'Kaydediliyor...' : 'Destek talebi oluştur'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-xl-5 mb-4">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bx-list-ul"></i>
                <h4>Ticket Durumu</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : tickets.length > 0 ? (
                <div className="self-service-ticket-list">
                  {tickets.slice(0, 5).map((ticket) => (
                    <div key={ticket._id} className="self-service-ticket-card">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <h6 className="mb-1">{ticket.subject}</h6>
                          <small className="text-muted">
                            {new Date(ticket.createdAt).toLocaleDateString('tr-TR')}
                          </small>
                        </div>
                        <span
                          className={`badge ${
                            urgencyBadgeClass[ticket.urgency] || 'text-bg-light'
                          }`}
                        >
                          {statusLabels[ticket.status] || ticket.status}
                        </span>
                      </div>
                      <p className="mb-1 mt-2">{ticket.description}</p>
                      <small className="text-muted d-block">
                        {ticket.categoryLabel || ticket.category} - {ticket.ticketNumber}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modern-empty-state">
                  <i className="bx bx-check-shield"></i>
                  <p>Henüz açılmış IT destek talebiniz bulunmuyor.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div id="teknik-sss" className="col-xl-6 mb-4 scroll-margin-section">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-help-circle"></i>
                <h4>Teknik SSS</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="self-service-list">
                {techFaqs.map((faq) => (
                  <div key={faq.id} className="self-service-list-card">
                    <h6>{faq.question}</h6>
                    <p className="mb-0">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-6 mb-4">
          <div className="modern-widget h-100">
            <div className="modern-widget-header">
              <div className="modern-widget-title">
                <i className="bx bxs-phone-call"></i>
                <h4>Hızlı İletişim</h4>
              </div>
            </div>
            <div className="modern-widget-body">
              <div className="row g-3">
                {operationsContacts.map((contact) => (
                  <div key={contact.id} className="col-md-6">
                    <div className="corporate-contact-card h-100">
                      <div className="corporate-contact-icon">
                        <i className={contact.icon}></i>
                      </div>
                      <div>
                        <h6>{contact.title}</h6>
                        <p>{contact.detail}</p>
                        <small className="d-block">{contact.contact}</small>
                        <small>{contact.phone}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default SelfServicePage;
